// 提取独立 hooks，减少主页代码量
import { useSeo } from '@web3fe/infra';
import { useFm, useWallet, useToast, useParams } from '@web3/base-hooks';
import { appPath } from '@web3/base-utils';
import { useEffect, useCallback, useRef, useState } from 'react';
import { useActivityStore } from '@/store/activity';
import useStore from '@/store';
import { ActivityTag, activityInfo } from '@/types';
import { getUniFrame, WalletType } from '@web3/base-utils';
import { pageViewTrack } from '@web3/base-tracking';
import { Page } from '@/config/track-config';
import { useBybitWallet } from '@/hooks/wallet';
import { BybitWalletEx, prepareTaskType } from '@/config/common';
import { BybitWalletChain } from '@/types/walletType';
import { getRewardEligibility } from '@/services/index';

const backgroundImage = `${appPath}/images/activity/share-join1.png`;

export const useActivitySeo = () => {
  const fm = useFm();

  useSeo({
    // 实际的seo不在 scr 项目里渲染，所以就不加别的seo内容了
    title: fm('rewardsSeoTitle'),
    desc: fm('rewardsSeoDesc'),
    keywords: fm('airdropSeoKeyword'),
  });
};

export const useShare = () => {
  const { activityId } = useParams();
  const store = useActivityStore();
  const fm = useFm();

  // 分享功能
  const handleButtonClick = useCallback(() => {
    const link = `https://app.bybit.com/inapp?by_dp=bybitapp%3A%2F%2Fopen%2Froute%3FtargetUrl%3Dby-mini%253A%252F%252Fweb3_applet%252Fmps2_activity_detail%253FactivityId%253D${activityId}&by_web_link=${window.top.location.href || ''}`;

    store
      .fetchShortLink({
        originalUrl: link,
        publicNetwork: 1,
      })
      .then(async (res) => {
        const { share } = await getUniFrame();
        share?.setProperty(share?.properties.CONFIG, {
          theme: 'dark',
          link: res?.tinyUrl,
          content: `${store.activityDetail?.activityInfo.activityName}\n${store.activityDetail?.activityInfo.activityDesc}`,
          channels: ['facebook', 'twitter', 'whatsapp', 'telegram', 'linkedin'],
          posterStyle: ['vertical'],
          poster: [
            {
              // 海报模板
              template: 'default', //"copy-trading",
              // 海报数据
              templateData: {
                showLogo: false,
                backgroundImage: {
                  vertical:
                    store.activityDetail?.activityInfo.webShareImg ||
                    backgroundImage,
                },
                content: {
                  desc: `<div>
                        <div style=" display: flex; align-items: center; ">
                            ${store.activityDetail?.activityInfo.activityName}
                        </div>
                        <p style="width: 280px;font-size: 32px;line-height: 40px;color: #fff;letter-spacing: -0.01em;margin-top: 16px;">
                        ${store.activityDetail?.activityInfo.activityDesc}
                        </p>
                    </div>`,
                },
                bottom: {
                  showCode: false,
                  desc: fm('share_qr'),
                },
              },
            },
          ],
        });

        share?.setProperty(share?.properties.VISIBLE, true);
      });
    // shareData: any, type = 'activity' || 'task'
  }, [store.activityDetail, activityId]);

  return { handleButtonClick };
};

export const usePageData = () => {
  const {
    isLogin,
    isBybitWallet,
    walletIsReady,
    evmWalletInfo,
    chainList,
    walletId,
    walletType,
  } = useWallet();
  const { walletAddresses } = useBybitWallet();
  const setActivityChainInfo = useStore((state) => state.setActivityChainInfo);
  const store = useActivityStore();
  const [pageLoading, setPageLoading] = useState(true);
  const { activityId, activityTag: activityTagParam } = useParams();
  const { toastWarn } = useToast();
  const fm = useFm();

  const activityTag = Number(activityTagParam) || ActivityTag.hunter;

  useEffect(() => {
    // TODO hack to prevent activity 0 to flush info
    if (!activityId) return;
    if (!walletIsReady) return;

    if (isBybitWallet && !walletAddresses.length) return;

    const listQuery = {
      activityId: Number(activityId),
      walletAddress: '',
      walletType: '',
    };

    setActivityChainInfo(null);
    pageViewTrack({
      page_name: Page.AirdropArcadeDetail,
      activity_index: Number(activityId),
    });

    store
      .fetchActivityDetail({
        activityId: Number(activityId),
        chainId: evmWalletInfo.chainId || 1,
        walletId,
        walletType,
        walletAddress: walletAddresses.find(
          (item) => +item.chainId === (+evmWalletInfo.chainId || 1)
        )?.walletAddress,
      })
      .then(async (res) => {
        if (!res?.success) return;
        activityChianInfo(res.result.activityInfo);
        console.log('walletAddresses', walletAddresses);
        Promise.all([
          // 获取充值类型任务

          store.fetchDepositTaskList({
            ...listQuery,
            chainId: res.result.activityInfo.chainId,
            taskType: prepareTaskType,
            walletId,
            walletType,
            walletAddress: walletAddresses.find(
              (item) => +item.chainId === +res.result.activityInfo.chainId
            )?.walletAddress,
          }),
          // 获取其它类型任务
          store.fetchTaskList({
            ...listQuery,
            excludeTaskType: prepareTaskType,
            chainId: res.result.activityInfo.chainId,
            walletId,
            walletType,
            walletAddress: walletAddresses.find(
              (item) => +item.chainId === +res.result.activityInfo.chainId
            )?.walletAddress,
          }),
        ]).then(() => {
          setPageLoading(false);
        });
      });
    //  获取存量任务
    if (+activityTag < ActivityTag.hunter) {
      store.fetchTaskList({
        ...listQuery,
        excludeTaskType: prepareTaskType,
        walletId,
        walletType,
      });
    }
    // TODO 目前只加了activityId, activeTag 依赖
  }, [
    evmWalletInfo.address,
    isBybitWallet,
    walletIsReady,
    walletAddresses.length,
  ]);

  useEffect(() => {
    document.querySelector('html')?.classList.add('moly-theme-dark');
  }, []);

  // 通过活动chainId拿当前连接的钱包中对应的链信息（主要针对异构链拿地址）
  async function activityChianInfo(activityInfo: activityInfo) {
    if (!isLogin) return;
    let activityChains, activityChain;
    // Bybit托管钱包连接时, 目前主要是拿云钱包异构链地址上报数据使用
    if (walletType === WalletType.CLOUD && chainList?.length) {
      activityChains = chainList?.filter(
        (item: any) => +item.chainId === +activityInfo.chainId
      );
      activityChain = activityChains[0];
      setActivityChainInfo(activityChain);
    }
    //Bybit插件钱包连接时
    if (BybitWalletEx.includes(walletType)) {
      let bybitWalletInfo = {
        walletId: '',
        walletType: WalletType.CLOUD,
        addressChain: [] as BybitWalletChain[],
      };
      try {
        bybitWalletInfo =
          (await window.top?.bybitWallet?.request({
            method: 'getWalletInfo',
          })) || {};
        // 判断是否异构链
        activityChains = bybitWalletInfo.addressChain.filter(
          (chain) => +chain.chainId === activityInfo.chainId
        );
        activityChain = activityChains[0];
        // console.log(bybitWalletInfo, activityChain);
        // 当前连接的钱包中不存在活动链
        // if (!activityChain) {
        //   toastWarn(fm('switchToSupportedNet'));
        // }
        setActivityChainInfo(activityChain);
      } catch (e) {
        console.log(e);
      }
    }
  }

  return { pageLoading };
};

export const useEligibility = () => {
  const [eligibilityCount, setEligibilityCount] = useState(0);
  const { activityId } = useParams();
  const { walletId, walletType, isLogin } = useWallet();
  const { walletAddresses } = useBybitWallet();
  const store = useActivityStore();
  const chainId = store.activityDetail?.activityInfo?.chainId;

  const fetchEligibility = async () => {
    const { result, success } = await getRewardEligibility({
      chainId,
      walletId,
      walletType,
      walletAddress: walletAddresses.find((item) => +item.chainId === +chainId)
        ?.walletAddress,
      ...{
        activityId: activityId && +activityId,
      },
    });
    if (success) {
      setEligibilityCount(result.rewardsCount);
    }
  };

  useEffect(() => {
    if (!isLogin) return;
    if (!chainId) return;
    if (!walletAddresses.length) return;

    fetchEligibility();
  }, [isLogin, walletAddresses.length, chainId]);

  return {
    eligibilityCount,
  };
};
